/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  DateRange
} from '@ion/core/src/types/general';
import {
  DatePeriodType,
  DateRangeType
} from '@ion/core/src/types/services';
import {
  getDateRange,
  toDateISOString
} from '@ion/core/src/utils/DateUtil';
import {DateSelectorValues} from '../../types/types';

// defines the type definitions for date filter options map ...
export type DateFilterOptionsType = {
  [key in DateSelectorValues]: {
    label: string;
    range: DateRange;
  }
};

/**
*
* The useDateFilter provides the events and objects which is
*  required for the audit log filter
*
* ```typescript
*     import {useDateFilter} from '@ion/dls/src/editors/audit/useDateFilter';
*
*     const {selectedRange, setSelectedRange, handleSubmit } = useDateFilter();
*
* ```
*/
export const useDateFilter = () => {
  const [selectedDateValue, setSelectedDateValue]     = useState<string>();
  const [selectedRange, setSelectedRange]             = useState<DateRangeType>(null);
  const [selectedFilterValue, setSelectedFilterValue] =
    useState<DateSelectorValues>(DateSelectorValues.LastSeven);

  const handleSubmit = function(key, {fromDate, toDate}) {
    const range: DateRangeType = {
      startDate : toDateISOString(fromDate, true, true),
      endDate   : toDateISOString(toDate, true, true)
    };
    setSelectedRange(range);
  };

  function setPeriod() {
    const {startDate, endDate}: DatePeriodType = getDateRange(getDateFilterOptionsMap()[selectedFilterValue]?.range);
    setSelectedRange({
      startDate : toDateISOString(startDate, true, true), endDate : toDateISOString(endDate, true, true)
    });
  };

  useEffect(() => {
    setPeriod();
  }, []);

  // date filter options map
  const getDateFilterOptionsMap = (): DateFilterOptionsType => ({
    [DateSelectorValues.LastSeven] : {
      label : translate('common:time.lastDays', {number: 7}),
      range : DateRange.Week
    },
    [DateSelectorValues.LastThirty] : {
      label : translate('common:time.lastDays', {number: 30}),
      range : DateRange.Month
    },
    [DateSelectorValues.Live] : {
      label : translate('common:time.live'),
      range : DateRange.default
    },
    [DateSelectorValues.Today] : {
      label : translate('common:time.today'),
      range : DateRange.default
    },
    [DateSelectorValues.Yesterday] : {
      label : translate('common:time.yesterday'),
      range : DateRange.default
    },
    [DateSelectorValues.Custom] : {
      label : translate('common:time.custom'),
      range : DateRange.default
    }
  });

  return {
    selectedFilterValue,
    selectedDateValue,
    setSelectedDateValue,
    setSelectedFilterValue,
    selectedRange,
    setSelectedRange,
    handleSubmit
  };
};
